import { Box, 
  Typography, 
  TextField, 
  MenuItem
} from "@mui/material";

import { CONTAINER_DEPOSIT_OPTIONS } from "../../constants/Constants";
import renderInputMatchAdornment from "./InputMatchAdornment";
import ProductActions from "../../components/ProductActions";

export default function IdentificationSection({
  isEditing = false,
  values,
  setValues,
  error,
  unitsOfMeasure,
  cin7Product,
  shopifyProduct,
  shopifyB2bProduct,
  handleDismiss,
}) {
 
  return (
    <Box sx={{ mb: 4, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', position: 'relative' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Product Identification and Status
        </Typography>
        <Box>
          <ProductActions
            isEditing={isEditing}
            cin7Id={cin7Product?.productId}
            shopifyB2cId={shopifyProduct?.productId}
            shopifyB2bId={shopifyB2bProduct?.productId}
            handleDismiss={handleDismiss}
          />
        </Box>
      </Box>

      {/* ROW 1 — Name */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          label="Product name"
          value={values.title}
          onChange={(e) => setValues((prev) => ({ ...prev, title: e.target.value }))}
          slotProps={{
            input: renderInputMatchAdornment(isEditing, cin7Product?.productName, shopifyProduct?.title),
          }}
          required
          fullWidth
          size="small"
          error={error && !values.title}
          helperText="Required field"
        />
      </Box>

      {/* ROW 2 — SKU / Barcode / Cin7 Status / Shopify Status */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 2,
          mb: 2,
        }}
      >
        {/* SKU */}
        <Box>
          <TextField
            label="SKU"
            value={values.sku}
            onChange={(e) => setValues(prev => ({ ...prev, sku: e.target.value }))}
            slotProps={{
              input: renderInputMatchAdornment(isEditing, cin7Product?.sku, shopifyProduct?.variant?.sku),
            }}
            required
            fullWidth
            size="small"
            error={error && !values.sku}
            disabled={isEditing ? true : false }
            helperText="Required field"
          />
        </Box>

        {/* BARCODE */}
        <Box>
          <TextField
            label="Barcode"
            value={values.barcode}
            onChange={(e) => setValues((prev) => ({ ...prev, barcode: e.target.value }))}
            slotProps={{
              input: renderInputMatchAdornment(isEditing, cin7Product?.barcode, shopifyProduct?.variant?.barcode),
            }}
            fullWidth
            size="small"
          />
        </Box>

        {/* CIN7 STATUS */}
        <Box>
          <TextField
            select
            label="Cin7 Status"
            value={values.status}
            onChange={(e) => setValues((prev) => ({ ...prev, status: e.target.value }))}
            fullWidth
            required
            size="small"
            error={error && !values.status}
            helperText="Required field"
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Setup required">Setup required</MenuItem>
            <MenuItem value="Deprecated">Deprecated</MenuItem>
          </TextField>
        </Box>

        {/* SHOPIFY STATUS */}
        <Box>
          <TextField
            select
            label="Shopify Status"
            value={values.shopifyStatus}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, shopifyStatus: e.target.value }))
            }
            fullWidth
            required
            size="small"
            error={error && !values.shopifyStatus}
            helperText="Required field"
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </TextField>
        </Box>
      </Box>

      {/* ROW 3 — Vintage / Unit of Measure / Deposit SKU */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          mb: 2,
        }}
      >
        {/* VINTAGE */}
        <TextField
          label="Vintage"
          value={values.metafields?.vintage || ""}
          onChange={(e) =>
            setValues((prev) => ({
              ...prev,
              metafields: { ...(prev.metafields || {}), vintage: e.target.value },
            }))
          }
          fullWidth
          size="small"
        />

        {/* UNIT OF MEASURE */}
        <TextField
          select
          label="Unit of Measure"
          value={values.unitOfMeasure}
          onChange={(e) =>
            setValues((prev) => ({ ...prev, unitOfMeasure: e.target.value }))
          }
          fullWidth
          required
          size="small"
          error={error && !values.unitOfMeasure}
          helperText="Required field"
        >
          {(unitsOfMeasure || []).map((u) => (
            <MenuItem key={u.ID} value={u.Name}>
              {u.Name}
            </MenuItem>
          ))}
        </TextField>

        {/* CONTAINER DEPOSIT SKU */}
        <TextField
          select
          label="Container Deposit"
          value={values.metafields?.container_deposit_sku || ""}
          onChange={(e) =>
            setValues((prev) => ({
              ...prev,
              metafields: {
                ...(prev.metafields || {}),
                container_deposit_sku: e.target.value,
              },
            }))
          }
          required
          fullWidth
          size="small"
          error={error && !values.metafields?.container_deposit_sku}
          helperText="Required field"
        >
          {CONTAINER_DEPOSIT_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );
}